import type { AIRecommendation, PricingAdvice } from '../types'

/**
 * PricingAdvisor — ideal price, margin and max discount suggestions.
 */
export class PricingAdvisor {
  advise(input: {
    description: string
    purchasePrice: number
    currentSalePrice?: number
    targetMargin?: number
    category?: string
  }): PricingAdvice {
    const targetMargin = input.targetMargin ?? marginByCategory(input.category)
    const purchase = Math.max(0, input.purchasePrice)
    const idealPrice = purchase <= 0 ? 0 : round2(purchase / (1 - targetMargin / 100))
    const current = input.currentSalePrice ?? idealPrice
    const currentMargin =
      current > 0 ? round2(((current - purchase) / current) * 100) : 0

    // Max discount keeps at least 60% of target margin
    const floorMargin = targetMargin * 0.6
    const floorPrice = purchase <= 0 ? 0 : round2(purchase / (1 - floorMargin / 100))
    const maxDiscountPercent =
      current > 0 ? round2(Math.max(0, ((current - floorPrice) / current) * 100)) : 0

    const recommendations: AIRecommendation[] = [
      {
        id: crypto.randomUUID(),
        kind: 'price',
        title: 'Preço ideal sugerido',
        suggestion: `Praticar ${idealPrice.toFixed(2)} em ${input.description}`,
        reason: `Margem alvo ${targetMargin}% sobre custo ${purchase.toFixed(2)}`,
        confidence: 0.82,
        requiresConfirmation: true,
      },
      {
        id: crypto.randomUUID(),
        kind: 'margin',
        title: 'Margem ideal',
        suggestion: `Buscar margem de ${targetMargin}%`,
        reason:
          currentMargin < targetMargin
            ? `Margem atual ${currentMargin}% está abaixo da referência da categoria`
            : `Margem atual ${currentMargin}% já atende ou supera a referência`,
        confidence: 0.8,
        requiresConfirmation: true,
      },
      {
        id: crypto.randomUUID(),
        kind: 'discount',
        title: 'Desconto máximo recomendado',
        suggestion: `Não conceder mais que ${maxDiscountPercent}%`,
        reason: `Preserva margem mínima de ${floorMargin.toFixed(1)}% (60% da meta)`,
        confidence: 0.75,
        impact: `Preço piso aproximado ${floorPrice.toFixed(2)}`,
        requiresConfirmation: true,
      },
    ]

    return {
      idealPrice,
      idealMargin: targetMargin,
      maxDiscountPercent,
      reason: recommendations[0]!.reason,
      recommendations,
    }
  }
}

function marginByCategory(category?: string): number {
  const map: Record<string, number> = {
    notebook: 18,
    ssd: 22,
    monitor: 20,
    'placa de vídeo': 15,
    geral: 20,
  }
  if (!category) return 20
  return map[category.toLowerCase()] ?? 20
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const pricingAdvisor = new PricingAdvisor()
